import { cache } from '../../dealer-franchisee/cache-impl/cache-registry.js';
import { clone, compare } from '../../../webmodule-common/other/clone';
import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import { EventBoolean, EventNotify, NullPromise } from '../../../webmodule-common/other/ui/events';
import {
  FabricationJob,
  FabricationJobItem,
  FabricationJobState,
  OptimizationResult,
  QuoteItemFabricationStatusView,
  ResultOptimizationMutation
} from '../../api/fabrication-job-api';
import { getApiFactory } from '../../api/api-injector';
import { runEventNotify } from '../../../webmodule-common/other/array-helper';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store.js';

export interface ConfiguredKerf {
  straight: number;
  angle: number;
}

export function getConfiguredKerf(): ConfiguredKerf {
  const props = userDataStore.getFranchisee().properties;
  return {
    straight: parseFloat(props['optimizer-kerf-value'] ?? '0') || 0,
    angle: parseFloat(props['optimizer-kerf-angle-value'] ?? '0') || 0
  };
}

export class FabricationJobContainer {
  fabricationJobId: string;
  fabricationJob: FabricationJob | null;
  items: FabricationJobItem[];
  optimizationResult: OptimizationResult | null;
  itemFabrications: QuoteItemFabricationStatusView[];

  constructor(
    fabricationJobId: string,
    fabricationJob: FabricationJob | null,
    items: FabricationJobItem[],
    optimizationResult: OptimizationResult | null = null,
    itemFabrications: QuoteItemFabricationStatusView[] = []
  ) {
    this.fabricationJobId = fabricationJobId;
    this.fabricationJob = fabricationJob;
    this.items = items;
    this.optimizationResult = optimizationResult;
    this.itemFabrications = itemFabrications;
  }
}

export class FabricationJobContainerManager {
  container: FabricationJobContainer;
  backup: FabricationJobContainer;
  api = getApiFactory().fabricationJob();
  afterSave: EventNotify[] = [];
  forceLocked?: EventBoolean;

  constructor(original: FabricationJobContainer) {
    this.container = original;
    this.backup = clone(this.container);
  }

  get fabricationJobId(): string {
    return this.container.fabricationJobId;
  }

  get fabricationJob(): FabricationJob {
    if (!this.container.fabricationJob) throw new DevelopmentError('FabricationJob is null');
    return this.container.fabricationJob;
  }

  get items(): FabricationJobItem[] {
    return this.container.items;
  }

  get itemFabrications(): QuoteItemFabricationStatusView[] {
    return this.container.itemFabrications;
  }

  /** Fabrication view for one quote item, or undefined if it has no row. */
  itemFabrication(quoteItemId: string): QuoteItemFabricationStatusView | undefined {
    return this.container.itemFabrications.find(x => x.quoteItemId === quoteItemId);
  }

  get optimizationResult(): OptimizationResult | null {
    return this.container.optimizationResult;
  }

  get isDraft(): boolean {
    return this.fabricationJob.state === FabricationJobState.Draft;
  }

  isLockedFromUse(): boolean {
    return this.forceLocked?.() ?? false;
  }

  isReadOnly(): boolean {
    return this.isLockedFromUse() || !this.isDraft;
  }

  jobChanged(): boolean {
    const current = this.container.fabricationJob;
    const original = this.backup.fabricationJob;
    if (!current || !original) return false;
    return !compare(original, current);
  }

  async needsFabricationJob(): Promise<void> {
    if (this.container.fabricationJob) return;
    await this.loadFromServer();
  }

  async loadFromServer(): Promise<void> {
    const [jobResult, optResult] = await Promise.all([
      this.api.getFabricationJob({ id: this.fabricationJobId }),
      this.api.getOptimizationResult({ fabricationJobId: this.fabricationJobId })
    ]);
    if (jobResult) {
      this.container.fabricationJob = jobResult.fabricationJob;
      this.container.items = jobResult.items;
      this.container.itemFabrications = jobResult.itemFabrications ?? [];
    }
    this.container.optimizationResult = optResult?.optimizationResult ?? null;
    this.backup = clone(this.container);
  }

  async refreshOptimizationResult(): Promise<void> {
    const result = await this.api.getOptimizationResult({ fabricationJobId: this.fabricationJobId });
    this.container.optimizationResult = result?.optimizationResult ?? null;
  }

  async startOptimization(): NullPromise<ResultOptimizationMutation> {
    const kerf = getConfiguredKerf();
    const result = await this.api.startOptimization({
      fabricationJobId: this.fabricationJobId,
      customKerfStraight: kerf.straight,
      customKerfAngle: kerf.angle
    });
    if (result) {
      this.container.optimizationResult = result.optimizationResult ?? null;
    }
    return result;
  }

  async lockInOptimization(): NullPromise<ResultOptimizationMutation> {
    const opt = this.container.optimizationResult;
    if (!opt) return null;
    const result = await this.api.lockInOptimization({
      fabricationJobId: this.fabricationJobId,
      recordVersion: this.fabricationJob.recordVersion,
      optimizationResultRecordVersion: opt.recordVersion
    });
    if (result) {
      this.container.optimizationResult = result.optimizationResult ?? null;
      // Lock-in flips the job to Planned; refresh to pick up the new state.
      await this.loadFromServer();
      await this.invalidateProjectCaches(result.projectIds ?? []);
      await this.doAfterSave();
    }
    return result;
  }

  async cancelOptimization(): NullPromise<ResultOptimizationMutation> {
    const opt = this.container.optimizationResult;
    const result = await this.api.cancelOptimization({
      fabricationJobId: this.fabricationJobId,
      optimizationResultRecordVersion: opt?.recordVersion ?? ''
    });
    if (result) {
      this.container.optimizationResult = result.optimizationResult ?? null;
    }
    return result;
  }

  // Lock-in and delete trigger project-side writes on the backend; here we
  // just mark the caches stale so any open project view refetches.
  private async invalidateProjectCaches(projectIds: string[]): Promise<void> {
    if (projectIds.length === 0) return;
    const projectCache = cache().project;
    const resourceLinkCache = cache().projectResourceLink;
    await Promise.all(
      projectIds.flatMap(projectId => [projectCache.updateLocal(projectId), resourceLinkCache.updateLocal(projectId)])
    );
  }

  async updateJob(): NullPromise<FabricationJob> {
    const job = this.fabricationJob;
    const result = await this.api.updateFabricationJob({
      id: job.id,
      recordVersion: job.recordVersion,
      title: job.title,
      description: job.description,
      assignedToUserId: job.assignedToUserId
    });
    if (result) {
      this.container.fabricationJob = result.fabricationJob;
      this.backup = clone(this.container);
      await this.doAfterSave();
    }
    return result?.fabricationJob ?? null;
  }

  async transitionState(target: FabricationJobState): Promise<boolean> {
    const result = await this.api.transitionFabricationJobState({
      fabricationJobId: this.fabricationJob.id,
      targetState: target,
      recordVersion: this.fabricationJob.recordVersion
    });
    if (!result) return false;
    // The transition may have rewritten every item's fabrication status, so
    // reload rather than patching the job row in place.
    await this.loadFromServer();
    await this.doAfterSave();
    return true;
  }

  async recordDelivery(quoteItemIds: string[], deliveryDate: string, notes: string): Promise<boolean> {
    const result = await this.api.recordQuoteItemDelivery({ quoteItemIds, deliveryDate, notes });
    if (!result) return false;
    await this.loadFromServer();
    await this.doAfterSave();
    return true;
  }

  async setFactoryTrackingReference(quoteItemId: string, reference: string): Promise<boolean> {
    const result = await this.api.setQuoteItemFactoryTrackingReference({ quoteItemId, reference });
    if (!result) return false;
    await this.loadFromServer();
    await this.doAfterSave();
    return true;
  }

  async deleteJob(): Promise<boolean> {
    const result = await this.api.deleteFabricationJob({
      id: this.fabricationJob.id,
      recordVersion: this.fabricationJob.recordVersion
    });
    if (result) {
      await this.invalidateProjectCaches(result.projectIds ?? []);
      await this.doAfterSave();
      return true;
    }
    return false;
  }

  async addItem(quoteItemId: string): NullPromise<FabricationJob> {
    const result = await this.api.addQuoteItemToFabricationJob({
      fabricationJobId: this.fabricationJob.id,
      quoteItemId,
      fabricationJobRecordVersion: this.fabricationJob.recordVersion
    });
    if (result) {
      this.container.fabricationJob = result.fabricationJob;
      await this.loadFromServer(); // reload items
      await this.doAfterSave();
    }
    return result?.fabricationJob ?? null;
  }

  async removeItem(quoteItemId: string): NullPromise<FabricationJob> {
    const result = await this.api.removeQuoteItemFromFabricationJob({
      fabricationJobId: this.fabricationJob.id,
      quoteItemId,
      fabricationJobRecordVersion: this.fabricationJob.recordVersion
    });
    if (result) {
      this.container.fabricationJob = result.fabricationJob;
      await this.loadFromServer(); // reload items
      await this.doAfterSave();
    }
    return result?.fabricationJob ?? null;
  }

  protected async doAfterSave(): Promise<void> {
    await runEventNotify(this.afterSave);
  }
}
